import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from 'lucide-react';


const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type FillType = 'copy' | 'distribute';
export type FillDirection = 'right' | 'down';
export type HorizontalFillDirection = 'right' | 'left';
export type BudgetType = 'resource' | 'cost' | 'sales';

interface FillSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceValue: number;
  sourceMonth?: number; // 0 to totalMonths-1
  direction: FillDirection;
  onApply: (fillType: FillType, toIndex: number, horizontalDirection?: HorizontalFillDirection) => void;
  totalMonths?: number; // Total number of months in the project
  monthLabels?: string[]; // Optional custom month labels with year
  budgetType?: BudgetType; // Type of budget (resource, cost, or sales)
}

export function FillSheet({
  open,
  onOpenChange,
  sourceValue,
  sourceMonth = 0,
  direction,
  onApply,
  totalMonths = 12,
  monthLabels,
  budgetType = 'resource',
}: FillSheetProps) {
  // Helper functions for dynamic text based on budget type
  const getBudgetLabel = () => {
    switch (budgetType) {
      case 'resource': return 'Resource Budget';
      case 'cost': return 'Cost Budget';
      case 'sales': return 'Sales Budget';
      default: return 'Budget';
    }
  };

  const getValueLabel = () => {
    switch (budgetType) {
      case 'resource': return 'hours';
      case 'cost': return '';
      case 'sales': return '';
      default: return '';
    }
  };

  const formatValue = (value: number) => {
    if (budgetType === 'resource') {
      return `${value.toFixed(2)} hours`;
    }
    // For cost and sales, use currency formatting
    return `£${value.toFixed(2)}`;
  };

  const formatValueOnly = (value: number) => {
    if (budgetType === 'resource') {
      return value.toFixed(2);
    }
    // For cost and sales, use currency formatting
    return `£${value.toFixed(2)}`;
  };

  // Determine which directions are valid based on source column position
  const isFirstColumn = sourceMonth === 0;
  const isLastColumn = sourceMonth === totalMonths - 1;
  const canFillLeft = !isFirstColumn;
  const canFillRight = !isLastColumn;
  
  // Determine initial direction based on available options
  const getInitialDirection = (): HorizontalFillDirection => {
    if (isLastColumn) return 'left'; // Can only go left
    return 'right'; // Default to right (or only option if first column)
  };
  
  const [fillType, setFillType] = useState<FillType>('distribute');
  const [horizontalFillDirection, setHorizontalFillDirection] = useState<HorizontalFillDirection>(getInitialDirection());
  const [toMonth, setToMonth] = useState<number>(
    getInitialDirection() === 'right' ? totalMonths - 1 : 0
  );
  
  // Update direction when source month changes
  useEffect(() => {
    const newDirection = getInitialDirection();
    setHorizontalFillDirection(newDirection);
    setToMonth(newDirection === 'right' ? totalMonths - 1 : 0);
  }, [sourceMonth, totalMonths]);
  
  // Update toMonth default when horizontalFillDirection changes
  const handleDirectionChange = (newDirection: HorizontalFillDirection) => {
    setHorizontalFillDirection(newDirection);
    // Reset toMonth to appropriate default
    if (newDirection === 'right') {
      setToMonth(totalMonths - 1); // Default to last month
    } else {
      setToMonth(0); // Default to first month
    }
  };


  const calculatePreview = () => {
    if (direction !== 'right') return [];
    
    // Determine the range based on fill direction
    const startMonth = horizontalFillDirection === 'right' ? sourceMonth : toMonth;
    const endMonth = horizontalFillDirection === 'right' ? toMonth : sourceMonth;
    const monthCount = Math.abs(endMonth - startMonth) + 1;
    const preview: { month: string; value: string }[] = [];
    
    // Use custom labels if provided, otherwise fall back to MONTHS array
    const getMonthLabel = (index: number) => {
      if (monthLabels && monthLabels[index]) {
        return monthLabels[index];
      }
      // Fallback to MONTHS array (wrapping around if index > 11)
      return MONTHS[index % 12];
    };

    if (fillType === 'copy') {
      // Copy same value to each month
      for (let i = startMonth; i <= endMonth; i++) {
        preview.push({
          month: getMonthLabel(i),
          value: formatValueOnly(sourceValue),
        });
      }
    } else {
      // Distribute equally
      const valuePerMonth = sourceValue / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100; // Round down to 2dp
      const remainder = sourceValue - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        const value = isLast ? roundedValue + remainder : roundedValue;
        preview.push({
          month: getMonthLabel(i),
          value: formatValueOnly(value),
        });
      }
    }

    return preview;
  };

  const preview = calculatePreview();
  const totalPreview = preview.reduce((sum, p) => sum + parseFloat(p.value), 0);

  const handleApply = () => {
    onApply(fillType, toMonth, horizontalFillDirection);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Content that's shared between Sheet and Dialog
  const FormContent = () => (
    <div className="space-y-6">
      {/* Source info - Inline */}
      <div className="flex items-center gap-2">
        <h4 className="font-medium">Source Value:</h4>
        <span className="text-sm text-muted-foreground">
          <span className="font-semibold">{formatValue(sourceValue)}</span>
        </span>
      </div>

      {/* Fill Direction */}
      {direction === 'right' && (
        <div className="space-y-4">
          <div className="flex items-center gap-1.5">
            <h4 className="font-medium">Fill Direction</h4>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Choose whether to fill cells to the right (forward in time) or to the left (backward in time) from the source month.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <RadioGroup value={horizontalFillDirection} onValueChange={(value) => handleDirectionChange(value as HorizontalFillDirection)}>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="right" id="fill-right" disabled={!canFillRight} />
                <Label
                  htmlFor="fill-right"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Fill Right
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="left" id="fill-left" disabled={!canFillLeft} />
                <Label
                  htmlFor="fill-left"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Fill Left
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Fill range */}
      {direction === 'right' && (
        <div className="space-y-4">
          <h4 className="font-medium">Fill Range</h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">From:</span>
            <span className="font-medium">{monthLabels?.[sourceMonth] || MONTHS[sourceMonth % 12]}</span>
            <span className="text-muted-foreground">To:</span>
            <Select
              value={toMonth.toString()}
              onValueChange={(value) => setToMonth(parseInt(value))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: totalMonths }, (_, index) => index).map((index) => (
                  // Show months >= sourceMonth for Fill Right, <= sourceMonth for Fill Left
                  (horizontalFillDirection === 'right' ? index >= sourceMonth : index <= sourceMonth) && (
                    <SelectItem key={index} value={index.toString()}>
                      {monthLabels?.[index] || MONTHS[index % 12]}
                    </SelectItem>
                  )
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Fill type */}
      <div className="space-y-4">
        <h4 className="font-medium">Fill Type</h4>
        <RadioGroup value={fillType} onValueChange={(value) => setFillType(value as FillType)}>
          <div className="flex items-start space-x-2">
            <RadioGroupItem value="copy" id="copy" />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="copy"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Copy value
              </label>
              <p className="text-sm text-muted-foreground">
                Copy {formatValue(sourceValue)} to each month
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <RadioGroupItem value="distribute" id="distribute" />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="distribute"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Distribute equally
              </label>
              <p className="text-sm text-muted-foreground">
                Distribute {formatValue(sourceValue)} evenly across selected months
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Preview */}
      {preview.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium">Preview</h4>
          <div className="rounded-md border bg-muted/30">
            {/* Scrollable preview cells - max height of ~4 rows */}
            <ScrollArea className="max-h-[200px]">
              <ScrollAreaViewport className="max-h-[200px]">
                <div className="flex flex-wrap gap-2 p-3">
                  {preview.map((p, i) => (
                    <div
                      key={i}
                      className="px-2 py-1 rounded border bg-primary/10 border-primary/30 text-center min-w-[70px]"
                    >
                      <span className="text-xs font-medium text-muted-foreground block">{p.month}</span>
                      <span className="text-sm font-medium">{p.value}</span>
                    </div>
                  ))}
                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
            {/* Fixed Total footer */}
            <div className="px-3 py-3 border-t flex justify-between text-sm font-bold">
              <span>Total:</span>
              <span>{formatValue(totalPreview)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const FooterButtons = () => (
    <>
      <Button variant="outline" onClick={handleCancel} className="flex-1">
        Cancel
      </Button>
      <Button onClick={handleApply} className="flex-1">
        Apply Fill
      </Button>
    </>
  );

  // Render as Sheet
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md flex flex-col p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader className="px-6 pt-6 pb-4">
          <div>
            <SheetTitle className="text-[20px] font-semibold">Fill {getBudgetLabel()}</SheetTitle>
            <SheetDescription className="text-sm">
              {direction === 'right' 
                ? `Fill ${getValueLabel() || 'values'} across months`.trim()
                : `Fill ${getValueLabel() || 'values'} down across roles`.trim()}
            </SheetDescription>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <FormContent />
        </div>

        <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto flex-row gap-2">
          <FooterButtons />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
